import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * OOS Alternative Products Component
 * Manages visibility of alternative product recommendations based on variant availability.
 */
class OosAlternativeProducts extends HTMLElement {
  /** @type {HTMLElement | null} */
  #blockContainer = null;

  /** @type {HTMLElement | null} */
  #blockWrapper = null;

  /** @type {string | null} */
  #productId = null;

  /** @type {((event: VariantUpdateEvent) => void) | null} */
  #variantUpdateHandler = null;

  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    this.#blockContainer = this.querySelector('.oos-alternative-products');
    if (!this.#blockContainer) return;

    this.#blockWrapper = this.closest('.oos-alternative-products-block');
    this.#productId = this.dataset.productId || null;
    this.#closestSection = this.closest('.shopify-section, dialog');
    
    if (!this.#closestSection) return;

    this.#variantUpdateHandler = this.#handleVariantUpdate.bind(this);
    this.#closestSection.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#variantUpdateHandler));
    
    // Initialize visibility after DOM and variant data are ready
    setTimeout(() => this.#initializeOnPageLoad(), 100);
  }
  
  /**
   * Initializes visibility on page load based on URL variant parameter
   */
  #initializeOnPageLoad() {
    // Sync block wrapper visibility with Liquid's initial state
    const initialHidden = this.dataset.initialHidden === 'true';
    if (this.#blockWrapper) {
      this.#blockWrapper.classList.toggle('hidden', initialHidden || this.hidden);
    }
    
    // Check URL variant parameter
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (!urlVariantId) return;
    
    const variant = this.#getVariantData(urlVariantId);
    if (variant) {
      this.#updateVisibility(variant);
    }
  }
  
  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event?.detail) return;

    const eventProductId = event.detail.data?.productId;
    if (eventProductId && this.#productId && eventProductId !== this.#productId) {
      return;
    }

    if (event.detail.data?.newProduct) {
      this.#productId = event.detail.data.newProduct.id;
    }

    this.#updateVisibility(event.detail.resource);
  };

  /**
   * Gets variant data by variant ID
   * @param {string|number} variantId
   * @returns {Object|null}
   */
  #getVariantData(variantId) {
    const win = /** @type {Window & {product?: {variants?: any[]}}} */ (window);
    if (win.product?.variants) {
      const variant = win.product.variants.find((v) => 
        v.id?.toString() === variantId.toString()
      );
      if (variant) return variant;
    }
    
    const variantPicker = this.closest('variant-picker') || 
                          document.querySelector(`variant-picker[data-product-id="${this.#productId}"]`);
    
    if (variantPicker) {
      const option = variantPicker.querySelector(`[data-variant-id="${variantId}"]`);
      if (option instanceof HTMLElement) {
        return {
          id: variantId,
          available: option.dataset.optionAvailable !== 'false'
        };
      }
    }
    
    return null;
  }

  /**
   * Updates block visibility based on variant availability
   * @param {Object | null} variant
   */
  #updateVisibility(variant) {
    if (!this.#blockContainer) return;

    const isOutOfStock = !variant || 
                        (variant && 'available' in variant && 
                        (variant.available === false || variant.available === 'false'));
    const hasAlternativeProducts = 
      this.#blockContainer.querySelector('.oos-alternative-products__carousel-wrapper, .oos-alternative-products__placeholder') !== null;

    const shouldShow = isOutOfStock && hasAlternativeProducts;
    
    this.#blockContainer.hidden = !shouldShow;
    this.hidden = !shouldShow;
    
    if (this.#blockWrapper) {
      this.#blockWrapper.classList.toggle('hidden', !shouldShow);
    }
  }

  disconnectedCallback() {
    if (this.#closestSection && this.#variantUpdateHandler) {
      this.#closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }
    
    this.#variantUpdateHandler = null;
    this.#closestSection = null;
    this.#blockContainer = null;
    this.#blockWrapper = null;
  }
}

if (!customElements.get('oos-alternative-products')) {
  customElements.define('oos-alternative-products', OosAlternativeProducts);
}
